import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleTags?: string[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleTags,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = 'https://51heiliao.com';
  const finalUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const finalTitle = `${title} | 51黑料`;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || description;

  useEffect(() => {
    document.title = finalTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) linkEl.setAttribute('href', finalUrl);
    else {
      const newLink = document.createElement('link');
      newLink.setAttribute('rel', 'canonical');
      newLink.setAttribute('href', finalUrl);
      document.head.appendChild(newLink);
    }

    setMeta('og:title', finalOgTitle, true);
    setMeta('og:description', finalOgDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', finalUrl, true);
    setMeta('og:site_name', '51黑料', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', finalOgTitle);
    setMeta('twitter:description', finalOgDescription);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime, true);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime, true);
    if (articleTags) {
      articleTags.forEach((tag) => {
        const tagEl = document.createElement('meta');
        tagEl.setAttribute('property', 'article:tag');
        tagEl.setAttribute('content', tag);
        document.head.appendChild(tagEl);
      });
    }

    return () => {
      if (articleTags) {
        document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
      }
    };
  }, [finalTitle, description, keywords, finalUrl, finalOgTitle, finalOgDescription, ogType, ogImage, articlePublishedTime, articleModifiedTime, articleTags]);

  return null;
}